import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { OverdueInvoicesPanel } from "@/components/OverdueInvoicesPanel";
import { RevenueChart } from "@/components/RevenueChart";
import ClientForm from "@/components/ClientForm";
import { Button } from "@/components/ui/button";
import {
  Users,
  Globe,
  ShieldCheck,
  MapPin,
  Plus,
  Calendar as CalendarIcon,
  ClipboardList,
  Video,
  ArrowRight,
  Mic,
  Sparkles,
  ListChecks,
  CheckCircle2,
  Circle,
  MessageCircle,
  PieChart,
  Mail,
  CalendarClock,
  Search,
  UserPlus,
  Building2,
  Loader2,
  X,
  AlertTriangle,
} from "lucide-react";

const greetings = { Poland: "Dzień dobry", Kenya: "Good morning" };

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.35, delay: i * 0.07 } }),
};

const fmtTime = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggConnected, setSuggConnected] = useState(true);
  const [research, setResearch] = useState({});
  const [researching, setResearching] = useState(null);
  const [adding, setAdding] = useState(null);
  const [expandedSugg, setExpandedSugg] = useState(null);
  const [actionItems, setActionItems] = useState([]);
  const [aiCount, setAiCount] = useState(0);
  const [setup, setSetup] = useState({ tasks: [], done: 0, total: 0 });
  const [sources, setSources] = useState({ sources: [], total: 0 });
  const [countries, setCountries] = useState({ countries: [], total: 0 });
  const [gact, setGact] = useState(null);
  const [expandedEmail, setExpandedEmail] = useState(null);
  const [emailBodies, setEmailBodies] = useState({});
  const [loadingBody, setLoadingBody] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const loadAll = () => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/insights/suggested-contacts").then((r) => { setSuggestions(r.data.suggestions || []); setSuggConnected(r.data.connected); }).catch(() => {});
    api.get("/dashboard/action-items").then((r) => { setActionItems(r.data.items); setAiCount(r.data.count); }).catch(() => {});
    api.get("/dashboard/setup-tasks").then((r) => setSetup(r.data)).catch(() => {});
    api.get("/dashboard/client-sources").then((r) => setSources(r.data)).catch(() => {});
    api.get("/dashboard/client-countries").then((r) => setCountries(r.data)).catch(() => {});
    api.get("/google/my-activity").then((r) => setGact(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const toggleEmail = async (id) => {
    if (expandedEmail === id) { setExpandedEmail(null); return; }
    setExpandedEmail(id);
    if (!emailBodies[id]) {
      setLoadingBody(true);
      try {
        const r = await api.get(`/google/message/${id}`);
        setEmailBodies((b) => ({ ...b, [id]: r.data }));
      } catch { setEmailBodies((b) => ({ ...b, [id]: { error: true } })); }
      finally { setLoadingBody(false); }
    }
  };

  const firstPending = setup.tasks.find((t) => t.status !== "done");

  const region = user?.region;
  const greeting = region ? greetings[region] : "Welcome";
  const firstName = user?.name?.split(" ")[0] || "";

  const meetings = gact?.events || [];

  const researchContact = async (s) => {
    if (research[s.email]) { setExpandedSugg(expandedSugg === s.email ? null : s.email); return; }
    setResearching(s.email); setExpandedSugg(s.email);
    try {
      const r = await api.post("/insights/research-contact", { email: s.email, from_name: s.from_name, domain: s.domain, company_guess: s.company_guess });
      setResearch((p) => ({ ...p, [s.email]: r.data }));
    } catch { toast.error("Nie udało się zbadać firmy"); setExpandedSugg(null); }
    finally { setResearching(null); }
  };

  const addContact = async (s) => {
    setAdding(s.email);
    const r = research[s.email];
    const notes = r
      ? `${r.summary || ""}\n\nBranża: ${r.industry || "—"}\nSynergia: ${r.synergy?.headline || "—"} (${r.synergy?.best_match_company || "—"}, ${r.synergy?.synergy_score ?? 0}/100)\n${r.synergy?.rationale || ""}`.trim()
      : "";
    try {
      const res = await api.post("/insights/add-contact", {
        company_name: r?.company_name || s.company_guess,
        email: s.email,
        contact_person: s.from_name,
        region: r?.region_guess || user?.region || "Kenya",
        country: r?.country || "",
        notes,
        synergy_best_match: r?.synergy?.best_match_company || "",
        synergy_score: r?.synergy?.synergy_score || 0,
        synergy_headline: r?.synergy?.headline || "",
        synergy_rationale: r?.synergy?.rationale || "",
        web_research_live: !!r?.web_research_live,
        web_sources: r?.web_sources || [],
      });
      toast.success(`Dodano do CRM: ${res.data.company_name}${r?.synergy?.best_match_company ? " · synergia zapisana" : ""}`);
      setSuggestions((prev) => prev.filter((x) => x.email !== s.email));
    } catch (e) { toast.error(e?.response?.data?.detail || "Nie udało się dodać kontaktu"); }
    finally { setAdding(null); }
  };

  const ignoreContact = async (s) => {
    try {
      await api.post("/insights/ignore-contact", { email: s.email });
      setSuggestions((prev) => prev.filter((x) => x.email !== s.email));
      toast.success("Zignorowano — kontakt nie będzie już proponowany");
    } catch { toast.error("Nie udało się zignorować kontaktu"); }
  };

  const MeetingRow = ({ e }) => (
    <div
      onClick={e.client_id ? () => navigate(`/clients/${e.client_id}`) : (e.meet_link ? () => window.open(e.meet_link, "_blank") : undefined)}
      data-testid={`meeting-event-${e.id}`}
      className={`w-full flex items-center gap-4 px-5 py-3.5 transition-colors text-left group ${(e.client_id || e.meet_link) ? "hover:bg-zinc-50 cursor-pointer" : ""}`}
    >
      <div className="flex flex-col items-center justify-center h-11 w-14 rounded-md bg-[#193962]/5 text-[#193962] shrink-0">
        <span className="font-[Outfit] font-bold text-sm leading-none">{fmtTime(e.start)}</span>
        <span className="text-[10px] font-[Manrope] mt-0.5">{new Date(e.start).toLocaleDateString("pl-PL", { day: "numeric", month: "short" })}</span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-[Manrope] font-medium text-sm truncate">{e.summary}</p>
        {e.client_name && <p className="text-xs text-[#193962] font-[Manrope] truncate flex items-center gap-1"><Users className="h-3 w-3" /> {e.client_name}</p>}
      </div>
      {e.meet_link && (
        <span className="flex items-center gap-1.5 text-xs text-[#193962] font-[Manrope] opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <Video className="h-3.5 w-3.5" /> Join
        </span>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F6F7F9]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Header */}
        <motion.div initial="hidden" animate="show" variants={fadeUp} custom={0} className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-sm font-[Manrope] uppercase tracking-widest text-[#193962] mb-1.5">Command Center</p>
            <h1 className="font-[Outfit] font-bold tracking-tight text-4xl sm:text-5xl" data-testid="dashboard-greeting">
              {greeting}, {firstName}.
            </h1>
            <p className="text-base text-muted-foreground font-[Manrope] mt-2">
              {region ? `Your ${region} workspace at a glance.` : "Set your region to unlock localized data."}
            </p>
          </div>
          <Button
            onClick={() => setAddOpen(true)}
            data-testid="quick-add-client-btn"
            className="h-11 px-5 bg-[#193962] hover:bg-[#12294A] text-white rounded-md font-[Manrope] transition-all duration-200 hover:-translate-y-[1px] hover:shadow-lg"
          >
            <Plus className="h-4 w-4 mr-2" /> Quick Add Client
          </Button>
        </motion.div>

        {(user?.role === "Admin" || user?.role === "Manager") && <><RevenueChart /><OverdueInvoicesPanel /></>}

        {!region && (
          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={1} className="mt-8 border border-[#193962]/20 bg-[#193962]/5 rounded-md p-6 flex items-center justify-between flex-wrap gap-4" data-testid="no-region-banner">
            <div className="flex items-center gap-3">
              <MapPin className="h-5 w-5 text-[#193962]" />
              <p className="font-[Manrope] text-sm">Select your operating region to see your agenda and clients.</p>
            </div>
            <Button onClick={() => navigate("/profile")} data-testid="set-region-btn" className="bg-[#193962] hover:bg-[#12294A] text-white rounded-md">Set your region</Button>
          </motion.div>
        )}

        {/* Stat strip */}
        {setup.total > 0 && setup.done < setup.total && (
          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={1} className="mt-8 border border-[#C99734]/40 bg-[#C99734]/5 rounded-md overflow-hidden" data-testid="setup-widget">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#C99734]/20 flex-wrap gap-3">
              <div className="flex items-center gap-2.5">
                <ListChecks className="h-5 w-5 text-[#C99734]" />
                <div>
                  <h3 className="font-[Outfit] font-semibold tracking-tight">Dokończ konfigurację integracji</h3>
                  <p className="text-xs text-muted-foreground font-[Manrope]">{setup.done}/{setup.total} gotowe — uruchom pełną funkcjonalność Toren PATH CRM.</p>
                </div>
              </div>
              {firstPending && (
                <a href={firstPending.whatsapp_url} target="_blank" rel="noreferrer" data-testid="setup-cta" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1da851] text-white text-sm font-[Manrope] font-medium px-4 py-2 rounded-md transition-colors">
                  <MessageCircle className="h-4 w-4" /> Umów przez WhatsApp
                </a>
              )}
            </div>
            <div className="divide-y divide-[#C99734]/15">
              {setup.tasks.map((t) => (
                <div key={t.id} data-testid={`setup-task-${t.id}`} className="flex items-center gap-3 px-5 py-3">
                  <span className="h-6 w-6 rounded-full bg-[#193962] text-white text-xs font-[Manrope] font-bold flex items-center justify-center shrink-0">{t.priority}</span>
                  {t.status === "done" ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> : <Circle className="h-4 w-4 text-zinc-300 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <p className={`font-[Manrope] text-sm ${t.status === "done" ? "text-zinc-400 line-through" : "font-medium"}`}>{t.title}</p>
                    <p className="text-xs text-muted-foreground font-[Manrope]">{t.description}</p>
                  </div>
                  {t.status !== "done" && (
                    <a href={t.whatsapp_url} target="_blank" rel="noreferrer" data-testid={`setup-wa-${t.id}`} className="inline-flex items-center gap-1.5 text-[#25D366] hover:underline text-sm font-[Manrope] shrink-0">
                      <MessageCircle className="h-4 w-4" /> WhatsApp
                    </a>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Stat strip */}
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Users, label: "Active Clients", value: summary ? summary.active_clients : "—", sub: summary ? `of ${summary.total_clients} total` : "", testid: "stat-active-clients", gold: true },
            { icon: ClipboardList, label: "Pending Actions", value: aiCount, sub: "from AI notes", testid: "stat-pending-actions", gold: true },
            { icon: Globe, label: "Region", value: region || "—", sub: "operating market", testid: "stat-region" },
            { icon: ShieldCheck, label: "Your Role", value: user?.role, sub: "access level", testid: "stat-role" },
          ].map((s, i) => (
            <motion.div key={s.label} initial="hidden" animate="show" variants={fadeUp} custom={i + 1} data-testid={s.testid} className="border border-border bg-white rounded-md p-5 hover:border-zinc-300 transition-colors">
              <div className="flex items-center gap-2 text-muted-foreground">
                <s.icon className={`h-4 w-4 ${s.gold ? "text-[#C99734]" : ""}`} />
                <span className="text-sm font-[Manrope]">{s.label}</span>
              </div>
              <p className={`mt-3 font-[Outfit] font-bold text-3xl tracking-tight tabular-nums ${s.gold ? "text-[#C99734]" : ""}`}>{s.value}</p>
              <p className="text-xs text-muted-foreground font-[Manrope] mt-0.5">{s.sub}</p>
            </motion.div>
          ))}
        </div>

        {/* Client sources mini-chart */}
        <motion.div initial="hidden" animate="show" variants={fadeUp} custom={4} className="mt-6 border border-border bg-white rounded-md" data-testid="client-sources-widget">
          <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border">
            <PieChart className="h-4 w-4 text-[#193962]" />
            <h3 className="font-[Outfit] font-semibold tracking-tight">Skąd trafiają klienci</h3>
            <span className="text-xs text-muted-foreground font-[Manrope] ml-auto">{sources.total} klient{sources.total === 1 ? "" : "ów"}</span>
          </div>
          <div className="p-5 space-y-3">
            {sources.sources.length === 0 ? (
              <p className="text-sm text-muted-foreground font-[Manrope] text-center py-6">Brak danych o źródłach — dodaj klientów ze wskazanym źródłem kontaktu.</p>
            ) : (
              sources.sources.map((s, i) => {
                const max = Math.max(...sources.sources.map((x) => x.count));
                const pct = Math.round((s.count / max) * 100);
                const share = sources.total ? Math.round((s.count / sources.total) * 100) : 0;
                return (
                  <button key={s.source} type="button" onClick={() => navigate(`/clients?source=${encodeURIComponent(s.source)}`)} data-testid={`source-bar-${i}`} className="w-full flex items-center gap-3 text-left hover:opacity-80 transition-opacity">
                    <span className="w-32 sm:w-44 text-sm font-[Manrope] text-zinc-700 truncate shrink-0">{s.source}</span>
                    <div className="flex-1 h-6 bg-zinc-100 rounded overflow-hidden">
                      <div className={`h-full rounded transition-all duration-500 ${i === 0 ? "bg-[#C99734]" : "bg-[#193962]"}`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-16 text-right text-sm font-[Outfit] font-semibold tabular-nums shrink-0">{s.count} <span className="text-xs text-zinc-400 font-[Manrope]">· {share}%</span></span>
                  </button>
                );
              })
            )}
          </div>
        </motion.div>

        {/* Country distribution mini-chart (East Africa focus) */}
        <motion.div initial="hidden" animate="show" variants={fadeUp} custom={4} className="mt-6 border border-border bg-white rounded-md" data-testid="client-countries-widget">
          <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border">
            <Globe className="h-4 w-4 text-[#193962]" />
            <h3 className="font-[Outfit] font-semibold tracking-tight">Klienci wg kraju</h3>
            <span className="text-xs text-muted-foreground font-[Manrope] ml-auto">{countries.total} klient{countries.total === 1 ? "" : "ów"}</span>
          </div>
          <div className="p-5 space-y-3">
            {countries.countries.length === 0 ? (
              <p className="text-sm text-muted-foreground font-[Manrope] text-center py-6" data-testid="client-countries-empty">Brak danych — uzupełnij pole „Kraj" w kartotekach klientów.</p>
            ) : (
              countries.countries.map((c, i) => {
                const max = Math.max(...countries.countries.map((x) => x.count));
                const pct = Math.round((c.count / max) * 100);
                const share = countries.total ? Math.round((c.count / countries.total) * 100) : 0;
                return (
                  <button key={c.country} type="button" onClick={() => navigate(`/clients?country=${encodeURIComponent(c.country)}`)} data-testid={`country-bar-${i}`} className="w-full flex items-center gap-3 text-left hover:opacity-80 transition-opacity">
                    <span className="w-32 sm:w-44 text-sm font-[Manrope] text-zinc-700 truncate shrink-0">{c.country}</span>
                    <div className="flex-1 h-6 bg-zinc-100 rounded overflow-hidden">
                      <div className={`h-full rounded transition-all duration-500 ${i === 0 ? "bg-[#C99734]" : "bg-[#193962]"}`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-16 text-right text-sm font-[Outfit] font-semibold tabular-nums shrink-0">{c.count} <span className="text-xs text-zinc-400 font-[Manrope]">· {share}%</span></span>
                  </button>
                );
              })
            )}
          </div>
        </motion.div>

        {/* Google activity widget */}
        <motion.div initial="hidden" animate="show" variants={fadeUp} custom={5} className="mt-6 border border-border bg-white rounded-md" data-testid="google-activity-widget">
          <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border">
            <Mail className="h-4 w-4 text-[#193962]" />
            <h3 className="font-[Outfit] font-semibold tracking-tight">Ostatnia aktywność Google</h3>
            {gact?.connected && <span className="rounded-full text-[10px] uppercase tracking-wider px-2 py-0.5 bg-emerald-100 text-emerald-700 font-[Manrope] font-medium">Live</span>}
          </div>
          {!gact?.connected ? (
            <p className="px-5 py-8 text-center text-sm text-muted-foreground font-[Manrope]" data-testid="google-activity-disconnected">
              Połącz konto Google w karcie dowolnego klienta → zakładka <strong>Workspace</strong> → „Connect Google", aby zobaczyć tu swoje maile i wydarzenia.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
              <div className="p-5">
                <p className="text-xs uppercase tracking-wider text-zinc-400 font-[Manrope] mb-3">Skrzynka odbiorcza</p>
                {(gact.emails || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground font-[Manrope]">Brak wiadomości.</p>
                ) : gact.emails.map((m) => (
                  <div
                    key={m.id}
                    className="py-2 border-b border-border last:border-0"
                    data-testid={`gact-email-${m.id}`}
                  >
                    <div
                      onClick={() => toggleEmail(m.id)}
                      className="cursor-pointer hover:bg-zinc-50 -mx-2 px-2 py-0.5 rounded transition-colors"
                      data-testid={`gact-email-toggle-${m.id}`}
                    >
                      <p className={`text-sm font-[Manrope] ${expandedEmail === m.id ? "" : "truncate"} ${m.unread ? "font-semibold" : ""}`}>{m.subject}</p>
                      <p className="text-xs text-zinc-400 font-[Manrope] truncate">{m.from_name} · {m.snippet}</p>
                    </div>
                    {expandedEmail === m.id && (
                      <div className="mt-2 mb-1 rounded bg-zinc-50 border border-border p-3" data-testid={`gact-email-body-${m.id}`}>
                        {loadingBody && !emailBodies[m.id] ? (
                          <p className="text-xs text-muted-foreground font-[Manrope]">Ładowanie treści…</p>
                        ) : emailBodies[m.id]?.error ? (
                          <p className="text-xs text-red-500 font-[Manrope]">Nie udało się pobrać treści wiadomości.</p>
                        ) : (
                          <pre className="text-xs text-zinc-700 font-[Manrope] whitespace-pre-wrap break-words max-h-64 overflow-y-auto">{emailBodies[m.id]?.body || "(brak treści)"}</pre>
                        )}
                      </div>
                    )}
                    {m.client_id && (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/clients/${m.client_id}`); }}
                        className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#193962] font-[Manrope] font-medium hover:underline"
                        data-testid={`gact-email-client-${m.id}`}
                      >
                        <Users className="h-3 w-3" /> {m.client_name || "Klient"} <ArrowRight className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div className="p-5">
                <p className="text-xs uppercase tracking-wider text-zinc-400 font-[Manrope] mb-3 flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> Nadchodzące wydarzenia</p>
                {(gact.events || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground font-[Manrope]">Brak wydarzeń.</p>
                ) : gact.events.map((e) => (
                  <div
                    key={e.id}
                    onClick={e.client_id ? () => navigate(`/clients/${e.client_id}`) : undefined}
                    className={`py-2 border-b border-border last:border-0 ${e.client_id ? "cursor-pointer hover:bg-zinc-50 -mx-2 px-2 rounded transition-colors" : ""}`}
                    data-testid={`gact-event-${e.id}`}
                  >
                    <p className="text-sm font-[Manrope] font-medium truncate">{e.summary}</p>
                    <p className="text-xs text-zinc-400 font-[Manrope]">{new Date(e.start).toLocaleString("pl-PL", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                    {e.client_id && (
                      <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#193962] font-[Manrope] font-medium" data-testid={`gact-event-client-${e.id}`}>
                        <Users className="h-3 w-3" /> {e.client_name || "Klient"} <ArrowRight className="h-3 w-3" />
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>

        {/* Suggested contacts from email */}
        {suggConnected && (
          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={5} className="mt-6 border border-border bg-white rounded-md" data-testid="suggested-contacts-widget">
            <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border">
              <UserPlus className="h-4 w-4 text-[#193962]" />
              <h3 className="font-[Outfit] font-semibold tracking-tight">Sugerowane kontakty z e-maili</h3>
              <span className="rounded-full text-[10px] uppercase tracking-wider px-2 py-0.5 bg-emerald-100 text-emerald-700 font-[Manrope] font-medium">AI</span>
              {suggestions.length > 0 && <span className="ml-auto text-xs text-muted-foreground font-[Manrope]">{suggestions.length} do przejrzenia</span>}
            </div>
            {suggestions.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-muted-foreground font-[Manrope]" data-testid="suggested-contacts-empty">
                Brak nowych firm z korespondencji do dodania — wszyscy nadawcy są już w CRM.
              </p>
            ) : (
              <div className="divide-y divide-border">
                {suggestions.map((s) => {
                  const r = research[s.email];
                  const open = expandedSugg === s.email;
                  return (
                    <div key={s.email} className="px-5 py-3" data-testid={`suggested-contact-${s.email}`}>
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex items-center justify-center h-9 w-9 rounded-md bg-[#193962]/5 text-[#193962] shrink-0">
                          <Building2 className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-[Manrope] font-medium text-sm truncate">{r?.company_name || s.company_guess}</p>
                          <p className="text-xs text-zinc-400 font-[Manrope] truncate">{s.from_name} · {s.email} · {s.count} wiad.</p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => researchContact(s)} disabled={researching === s.email} data-testid={`research-btn-${s.email}`} className="shrink-0">
                          {researching === s.email ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Search className="h-3.5 w-3.5 mr-1" />} Zbadaj
                        </Button>
                        <Button size="sm" onClick={() => addContact(s)} disabled={adding === s.email} data-testid={`add-contact-btn-${s.email}`} className="shrink-0 bg-[#193962] hover:bg-[#12294A] text-white">
                          {adding === s.email ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <UserPlus className="h-3.5 w-3.5 mr-1" />} Dodaj
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => ignoreContact(s)} data-testid={`ignore-contact-btn-${s.email}`} title="Zignoruj — nie proponuj więcej" className="shrink-0 h-8 w-8 text-zinc-400 hover:text-red-600 hover:bg-red-50">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      {open && r && (
                        <div className="mt-3 rounded bg-zinc-50 border border-border p-3 space-y-2" data-testid={`research-panel-${s.email}`}>
                          <p className="text-sm text-zinc-700 font-[Manrope]">{r.summary}</p>
                          <p className="text-xs text-zinc-500 font-[Manrope]">
                            {r.industry && <>Branża: <strong>{r.industry}</strong> · </>}
                            {r.country && <>Kraj: <strong>{r.country}</strong> · </>}
                            Region: <strong>{r.region_guess}</strong>
                            {r.website && <> · <a href={r.website} target="_blank" rel="noreferrer" className="text-[#193962] hover:underline">{r.website}</a></>}
                          </p>
                          {r.synergy && (r.synergy.headline || r.synergy.best_match_company) && (
                            <div className="rounded bg-white border border-border p-2.5">
                              <p className="text-xs uppercase tracking-wider text-zinc-400 font-[Manrope] flex items-center gap-1.5"><Sparkles className="h-3 w-3" /> Potencjalna synergia · {r.synergy.synergy_score}/100</p>
                              <p className="text-sm font-[Manrope] font-medium mt-1">{r.synergy.headline}</p>
                              {r.synergy.best_match_company && <p className="text-xs text-[#193962] font-[Manrope]">z: {r.synergy.best_match_company}</p>}
                              <p className="text-xs text-zinc-600 font-[Manrope] mt-1">{r.synergy.rationale}</p>
                            </div>
                          )}
                          {r.degraded && (
                            <p className="text-[12px] text-red-600 font-[Manrope] flex items-center gap-1.5" data-testid={`research-degraded-${s.email}`}>
                              <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Analiza AI niedostępna — problem z usługą dostawcy (np. brak środków/limit). Spróbuj ponownie później.
                            </p>
                          )}
                          {!r.degraded && r.web_research_live === false && <p className="text-[11px] text-amber-600 font-[Manrope] flex items-center gap-1.5"><AlertTriangle className="h-3 w-3 shrink-0" /> Bez live-web (Tavily) — analiza wyłącznie AI.</p>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Agenda */}
          {/* Agenda */}
          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={5} className="lg:col-span-2 border border-border bg-white rounded-md" data-testid="agenda-widget">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div className="flex items-center gap-2.5">
                <CalendarIcon className="h-4 w-4 text-[#193962]" />
                <h3 className="font-[Outfit] font-semibold tracking-tight">Upcoming Meetings</h3>
                {gact?.connected && <span className="rounded-full text-[10px] uppercase tracking-wider px-2 py-0.5 bg-emerald-100 text-emerald-700 font-[Manrope] font-medium">Live</span>}
              </div>
              <span className="text-xs text-muted-foreground font-[Manrope]">{meetings.length} scheduled</span>
            </div>
            {!gact?.connected ? (
              <p className="px-5 py-12 text-center text-sm text-muted-foreground font-[Manrope]" data-testid="meetings-disconnected">
                Połącz konto Google w karcie dowolnego klienta → zakładka <strong>Workspace</strong> → „Connect Google", aby zobaczyć tu nadchodzące spotkania z Kalendarza.
              </p>
            ) : meetings.length === 0 ? (
              <p className="px-5 py-12 text-center text-sm text-muted-foreground font-[Manrope]">Brak nadchodzących spotkań w Kalendarzu.</p>
            ) : (
              <div className="divide-y divide-border">{meetings.map((e) => <MeetingRow key={e.id} e={e} />)}</div>
            )}
          </motion.div>

          {/* Action items */}
          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={6} className="border border-border bg-white rounded-md flex flex-col" data-testid="action-items-widget">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div className="flex items-center gap-2.5">
                <Sparkles className="h-4 w-4 text-[#193962]" />
                <h3 className="font-[Outfit] font-semibold tracking-tight">Pending Action Items</h3>
              </div>
              <button onClick={() => navigate("/meetings")} data-testid="view-meetings-link" className="text-xs text-[#193962] font-[Manrope] hover:underline flex items-center gap-1">
                Meetings <ArrowRight className="h-3 w-3" />
              </button>
            </div>
            {actionItems.length === 0 ? (
              <div className="px-5 py-12 text-center flex-1 flex flex-col items-center justify-center">
                <Mic className="h-7 w-7 text-zinc-300 mb-3" />
                <p className="text-sm text-muted-foreground font-[Manrope]">Upload a meeting recording — AI-extracted tasks appear here.</p>
              </div>
            ) : (
              <div className="divide-y divide-border overflow-y-auto max-h-[420px]">
                {actionItems.map((it, i) => (
                  <button key={i} onClick={() => navigate(`/clients/${it.client_id}`)} data-testid={`action-item-${i}`} className="w-full text-left px-5 py-3 hover:bg-zinc-50 transition-colors">
                    <div className="flex items-start gap-2.5">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#193962] shrink-0" />
                      <div className="min-w-0">
                        <p className="font-[Manrope] text-sm text-zinc-800 leading-snug">{it.task}</p>
                        <p className="text-xs font-[Manrope] mt-0.5">
                          <span className="text-[#193962]">{it.assignee}</span>
                          <span className="text-zinc-400"> · {it.client_name}</span>
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </main>

      <ClientForm open={addOpen} onOpenChange={setAddOpen} client={null} onSaved={loadAll} />
    </div>
  );
}
